import axios, { AxiosError } from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/auth';
import { useProjectStore } from '../stores/project';
import router from '@/router';
import {
  isRefreshExpiryCredentialReconciliationPending,
  markRefreshExpiryCredentialReconciliationPending,
  purgeAdamCloudStorage,
} from '@/shared/utils/authSessionCleanup';
import {
  isProblemDetails,
  isValidationErrorResponse,
  isApiErrorResponse,
  addVersionHeaders,
  buildQueryParams
} from './http-helpers';
import { logger, generateCorrelationId } from './logging';
import type { ApiLogMetadata } from './logging';

/**
 * Extended request config with logging metadata.
 */
interface RequestMetadata {
  startTime: number;
  correlationId: string;
}

interface ClearClientAuthSessionOptions {
  deferStoragePurge?: boolean;
}

/**
 * Augment InternalAxiosRequestConfig with metadata for logging.
 */
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: RequestMetadata;
  }
}
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};


const requestInterceptor = async (config: InternalAxiosRequestConfig) => {
  const authStore = useAuthStore();
  const projectStore = useProjectStore();

  // Add logging metadata for request timing and correlation
  config.metadata = {
    startTime: Date.now(),
    correlationId: generateCorrelationId(),
  };

  if (authStore.accessToken) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${authStore.accessToken}`;
  }

  // Add X-Project-Id header if there's a selected project
  if (projectStore.selectedProjectId) {
    config.headers = config.headers || {};
    config.headers['X-Project-Id'] = projectStore.selectedProjectId;
  }

  return config;
};

// Enhanced error transformation for different API response types
const transformApiError = (error: AxiosError): AxiosError => {
  if (error.response && error.response.data) {
    const errorData = error.response.data;

    // Try to extract a more specific error message from the response data
    let specificMessage = '';
    if (typeof errorData === 'string' && errorData.length > 0) {
      specificMessage = errorData;
    } else if (typeof errorData === 'object' && errorData !== null) {
      const data = errorData as any;
      // Prefer 'detail' (ProblemDetails standard), then 'message' (common convention), then 'title'
      if (typeof data.detail === 'string' && data.detail) {
        specificMessage = data.detail;
      } else if (typeof data.message === 'string' && data.message) {
        specificMessage = data.message;
      } else if (typeof data.title === 'string' && data.title && !isValidationErrorResponse(errorData)) {
        // Use title if available, but avoid generic titles for validation errors
        specificMessage = data.title;
      }
    }

    // Create enhanced error with structured data
    const enhancedError = new AxiosError(
      specificMessage || error.message,
      error.code,
      error.config,
      error.request,
      error.response
    );

    // Add structured error information based on type (check most specific first)
    if (isValidationErrorResponse(errorData)) {
      enhancedError.name = 'ValidationError';
      (enhancedError as any).validationErrors = errorData.errors;
      (enhancedError as any).problemDetails = errorData;
    } else if (isApiErrorResponse(errorData)) {
      enhancedError.name = 'ApiError';
      (enhancedError as any).apiError = errorData;
    } else if (isProblemDetails(errorData)) {
      enhancedError.name = 'ProblemDetailsError';
      (enhancedError as any).problemDetails = errorData;
    }

    return enhancedError;
  }

  return error;
};


const responseInterceptor = async (error: AxiosError) => {
  const authStore = useAuthStore();
  const projectStore = useProjectStore();
  const originalRequest: any = error.config;

  const clearClientAuthSession = ({ deferStoragePurge = false }: ClearClientAuthSessionOptions = {}) => {
    authStore.clearToken();
    projectStore.resetSelectionValidation();

    if (deferStoragePurge) {
      markRefreshExpiryCredentialReconciliationPending();
      return;
    }

    if (isRefreshExpiryCredentialReconciliationPending()) {
      return;
    }

    purgeAdamCloudStorage();
  };

  // If the refresh token is invalid/expired, the refresh endpoint will 401.
  // In that case we must log out and reject all queued requests (no retry).
  const requestUrl = typeof originalRequest?.url === 'string' ? originalRequest.url : '';
  const isRefreshRequest = requestUrl.includes('/auth/refresh');
  if (error.response?.status === 401 && isRefreshRequest) {
    markRefreshExpiryCredentialReconciliationPending();
    processQueue(error, null);
    clearClientAuthSession({ deferStoragePurge: true });
    router.push({ path: '/login' });
    isRefreshing = false;
    return Promise.reject(transformApiError(error));
  }

  // If a project-scoped request 404s, the cached selected project likely no longer exists.
  // Clear selection and route to project selection page.
  if (error.response?.status === 404) {
    const projectHeader = originalRequest?.headers?.['X-Project-Id'];
    const hasProjectScope = typeof projectHeader === 'string' && projectHeader.length > 0;

    // Only treat 404s as "project not found" when the request appears to target a project-scoped
    // backend endpoint. Many endpoints (for example: documents for an asset) may legitimately
    // return 404 to mean "no related data" and should not cause global project-clearing.
    const requestUrl = typeof originalRequest?.url === 'string' ? originalRequest.url : '';

    function isProjectScopedUrl(url: string): boolean {
      // Normalize to lower-case for simple checks but preserve original for other checks
      if (!url) return false;
      const u = url.toLowerCase();

      // Common patterns for project-scoped endpoints
      if (u.startsWith('/projects') || u.includes('/projects/')) return true;
      if (u.startsWith('/api/projects') || u.includes('/api/projects/')) return true;

      // Also consider endpoints that explicitly include a project identifier header but do not
      // belong to project resource paths as non-project-scoped. Keep this conservative: only
      // treat clearly project-related paths as project-scoped.
      return false;
    }

    if (hasProjectScope && isProjectScopedUrl(requestUrl)) {
      projectStore.clearSelectedProjectCache();
      if (router.currentRoute.value.path !== '/home/project-selection') {
        router.push({ path: '/home/project-selection' });
      }
    }
  }

  if (
    error.response &&
    error.response.status === 401 &&
    authStore.refreshToken &&
    !originalRequest._retry &&
    !isRefreshRequest
  ) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (typeof token === 'string') {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return ApiService(originalRequest);
          }
          return Promise.reject(transformApiError(error));
        })
        .catch(err => Promise.reject(transformApiError(err)));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await ApiService.post('/auth/refresh', { refreshToken: authStore.refreshToken });
      // Accept both { accessToken } (new) and { token } (legacy)
      const accessToken = (data as any)?.accessToken ?? (data as any)?.token ?? null;
      const newRefreshToken = (data as any)?.refreshToken ?? null;
      authStore.setToken(accessToken, newRefreshToken);
      processQueue(null, accessToken);
      if (accessToken) {
        originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
      }
      return ApiService(originalRequest);
    } catch (refreshError) {
      markRefreshExpiryCredentialReconciliationPending();
      processQueue(refreshError, null);
      clearClientAuthSession({ deferStoragePurge: true });
      router.push({ path: '/login' });
      return Promise.reject(transformApiError(refreshError as AxiosError));
    } finally {
      isRefreshing = false;
    }
  }

  if (error.response && error.response.status === 401) {
    clearClientAuthSession();
    router.push({ path: '/login' });
  }

  // Log the error before rejecting
  const transformedError = transformApiError(error);
  logApiError(error);

  // Apply error transformation for all errors
  return Promise.reject(transformedError);
};

/**
 * Success response interceptor for logging successful API calls.
 */
const successResponseInterceptor = (response: AxiosResponse): AxiosResponse => {
  const config = response.config;
  const metadata = config.metadata;

  if (metadata) {
    const duration = Date.now() - metadata.startTime;
    const logMetadata: ApiLogMetadata = {
      method: config.method?.toUpperCase() ?? 'UNKNOWN',
      url: config.url ?? '',
      status: response.status,
      statusText: response.statusText,
      duration,
    };

    // Log asynchronously to not block response
    logger.api(logMetadata, metadata.correlationId).catch(() => {
      // Silently ignore logging errors
    });
  }

  return response;
};

/**
 * Logs an API error with relevant details.
 */
function logApiError(error: AxiosError): void {
  const config = error.config;
  const metadata = config?.metadata;
  const response = error.response;

  const duration = metadata ? Date.now() - metadata.startTime : undefined;
  const correlationId = metadata?.correlationId;

  const logMetadata: ApiLogMetadata = {
    method: config?.method?.toUpperCase() ?? 'UNKNOWN',
    url: config?.url ?? '',
    status: response?.status,
    statusText: response?.statusText,
    duration,
    errorMessage: error.message,
    errorCode: error.code,
    responseData: response?.data,
  };

  // Log asynchronously to not block error handling
  logger.api(logMetadata, correlationId).catch(() => {
    // Silently ignore logging errors
  });
}

const ApiService: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 0,
});

ApiService.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error));
ApiService.interceptors.response.use(
  successResponseInterceptor,
  responseInterceptor
);

// Enhanced API service with support for version headers and better query handling
export const createApiRequest = {
  get: (url: string, params?: Record<string, any>, version?: string) => {
    const config: any = {};

    if (params || version) {
      config.params = buildQueryParams(params, { version });
    }

    if (version) {
      config.headers = addVersionHeaders({ version });
    }

    return ApiService.get(url, config);
  },

  post: (url: string, data?: any, params?: Record<string, any>, version?: string) => {
    const config: any = {};

    if (params || version) {
      config.params = buildQueryParams(params, { version });
    }

    if (version) {
      config.headers = addVersionHeaders({ version });
    }

    return ApiService.post(url, data, config);
  },

  put: (url: string, data?: any, params?: Record<string, any>, version?: string) => {
    const config: any = {};

    if (params || version) {
      config.params = buildQueryParams(params, { version });
    }

    if (version) {
      config.headers = addVersionHeaders({ version });
    }

    return ApiService.put(url, data, config);
  },

  delete: (url: string, params?: Record<string, any>, version?: string) => {
    const config: any = {};

    if (params || version) {
      config.params = buildQueryParams(params, { version });
    }

    if (version) {
      config.headers = addVersionHeaders({ version });
    }

    return ApiService.delete(url, config);
  }
};

export default ApiService;
